import { Filter } from 'lucide-react';
import React, { useRef, useState, type ReactNode } from 'react';
import { Button } from 'react-bootstrap';

interface CollapsibleFiltersProps {
  children: ReactNode;
  className?: string;
}

interface CollapsibleFiltersContextType {
  isOpen: boolean;
  toggleFilters: () => void;
}

const CollapsibleFiltersContext =
  React.createContext<CollapsibleFiltersContextType>({
    isOpen: false,
    toggleFilters: () => {},
  });

const useCollapsibleFilters = () => React.useContext(CollapsibleFiltersContext);

const CollapsibleFiltersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleFilters = () => {
    setIsOpen(!isOpen);
  };

  return (
    <CollapsibleFiltersContext.Provider value={{ isOpen, toggleFilters }}>
      {children}
    </CollapsibleFiltersContext.Provider>
  );
};

const CollapsibleFiltersButton: React.FC = () => {
  const { isOpen, toggleFilters } = useCollapsibleFilters();

  return (
    <Button
      variant="link"
      className="p-0 text-dark"
      onClick={toggleFilters}
      aria-label="Toggle filters"
      style={{ width: '24px', height: '24px', marginTop: '-6px' }}
    >
      <Filter
        size={20}
        style={{
          transition: 'transform 0.2s ease-in-out',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </Button>
  );
};

const CollapsibleFiltersContent: React.FC<CollapsibleFiltersProps> = ({
  children,
  className = '',
}) => {
  const { isOpen } = useCollapsibleFilters();
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={`w-100 ${className}`}
      style={{
        maxHeight: isOpen
          ? `${contentRef.current?.scrollHeight || 0}px`
          : '0px',
        transition: 'max-height 0.3s ease-in-out',
        overflow: isOpen ? 'visible' : 'hidden',
      }}
    >
      <div
        ref={contentRef}
        className="px-4 pb-3"
        style={{
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const CollapsibleFilters = {
  Provider: CollapsibleFiltersProvider,
  Button: CollapsibleFiltersButton,
  Content: CollapsibleFiltersContent,
};

export default CollapsibleFilters;
